import { parseUnits } from '@ethersproject/units'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  isSwapEnabledForCurrency,
  SWAP_DISABLED_ERROR,
  tradeInvolvesSwapDisabledToken,
} from '../../config/tokens/productToken'
import {
  getNativeChainIdForCurrency,
  nativeCurrencyId,
} from '../../config/native'
import { useSessionCurrency } from '../../features/swap/useSessionCurrency'
import { ChainId, Currency, CurrencyAmount, JSBI, Token, TokenAmount, Trade } from '../../sdk'
import { useAppChainId } from '../../domain/wallet'
import { useAppWeb3 } from '../../hooks'
import { normalizeSwapAmountInput } from '../../utils/swapAmountDisplay'
import { useTradeExactIn, useTradeExactOut } from '../../hooks/Trades'
import { isAddress } from '../../utils'
import { AppDispatch, AppState } from '../index'
import { useCurrencyBalances } from '../wallet/hooks'
import { Field, selectCurrency, switchCurrencies, typeInput } from './actions'
import { useUserSlippageTolerance } from '../user/hooks'
import { computeSlippageAdjustedAmounts } from '../../utils/prices'

export { swapCurrencyIdsEquivalent, swapReplacePayloadMatchesState } from './swapStateHelpers'

export function useSwapState(): AppState['swap'] {
  return useSelector<AppState, AppState['swap']>((state) => state.swap)
}

export function useSwapActionHandlers(): {
  onCurrencySelection: (field: Field, currency: Currency) => void
  onSwitchTokens: () => void
  onUserInput: (field: Field, typedValue: string) => void
} {
  const dispatch = useDispatch<AppDispatch>()
  const onCurrencySelection = useCallback(
    (field: Field, currency: Currency) => {
      dispatch(
        selectCurrency({
          field,
          currencyId:
            currency instanceof Token
              ? currency.address
              : (() => {
                  const nativeChainId = getNativeChainIdForCurrency(currency)
                  return nativeChainId !== undefined ? nativeCurrencyId(nativeChainId) : ''
                })(),
        }),
      )
    },
    [dispatch],
  )

  const onSwitchTokens = useCallback(() => {
    dispatch(switchCurrencies())
  }, [dispatch])

  const onUserInput = useCallback(
    (field: Field, typedValue: string) => {
      dispatch(typeInput({ field, typedValue }))
    },
    [dispatch],
  )

  return {
    onSwitchTokens,
    onCurrencySelection,
    onUserInput,
  }
}

export function tryParseAmount(
  value?: string,
  currency?: Currency,
  chainId?: ChainId,
): CurrencyAmount | undefined {
  if (!value || !currency) {
    return undefined
  }
  const sanitizedValue = value.replace(/,/g, '').trim()
  if (!sanitizedValue || sanitizedValue === '.' || sanitizedValue.endsWith('.')) {
    return undefined
  }
  try {
    const typedValueParsed = parseUnits(sanitizedValue, currency.decimals).toString()
    if (typedValueParsed !== '0') {
      const nativeChainId =
        getNativeChainIdForCurrency(currency) ??
        (chainId != null && !(currency instanceof Token) ? chainId : undefined)
      if (nativeChainId !== undefined) {
        return CurrencyAmount.native(nativeChainId, JSBI.BigInt(typedValueParsed))
      }
      if (currency instanceof Token) {
        return new TokenAmount(currency, JSBI.BigInt(typedValueParsed))
      }
    }
  } catch {
    // should fail if the user specifies too many decimal places of precision (or maybe exceed max uint?)
  }
  return undefined
}

const BAD_RECIPIENT_ADDRESSES: string[] = [
  '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f',
  '0xf164fC0Ec4E93095b804a4795bBe1e041497b92a',
  '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D',
]

function involvesAddress(trade: Trade, checksummedAddress: string): boolean {
  return (
    trade.route.path.some((token) => token.address === checksummedAddress) ||
    trade.route.pairs.some((pair) => pair.liquidityToken.address === checksummedAddress)
  )
}

export function useDerivedSwapInfo(): {
  currencies: { [field in Field]?: Currency }
  currencyBalances: { [field in Field]?: CurrencyAmount }
  parsedAmount: CurrencyAmount | undefined
  v2Trade: Trade | undefined
  inputError?: string
} {
  const { account, chainId } = useAppWeb3()
  const appChainId = useAppChainId()

  const {
    independentField,
    typedValue,
    [Field.INPUT]: { currencyId: inputCurrencyId },
    [Field.OUTPUT]: { currencyId: outputCurrencyId },
  } = useSwapState()

  const inputCurrency = useSessionCurrency(inputCurrencyId || undefined)
  const outputCurrency = useSessionCurrency(outputCurrencyId || undefined)
  const to: string | null = account ?? null

  const relevantTokenBalances = useCurrencyBalances(account ?? undefined, [
    inputCurrency ?? undefined,
    outputCurrency ?? undefined,
  ])

  const isExactIn: boolean = independentField === Field.INPUT
  const amountCurrency = isExactIn ? inputCurrency : outputCurrency
  const normalizedTypedValue =
    amountCurrency != null ? normalizeSwapAmountInput(typedValue, amountCurrency.decimals) : typedValue
  const parsedAmount = tryParseAmount(
    normalizedTypedValue,
    amountCurrency ?? undefined,
    appChainId ?? chainId ?? undefined,
  )

  const bestTradeExactIn = useTradeExactIn(
    isExactIn ? parsedAmount : undefined,
    outputCurrency ?? undefined,
    inputCurrency ?? undefined,
  )
  const bestTradeExactOut = useTradeExactOut(
    inputCurrency ?? undefined,
    !isExactIn ? parsedAmount : undefined,
    outputCurrency ?? undefined,
  )

  const v2Trade = isExactIn ? bestTradeExactIn : bestTradeExactOut

  const currencyBalances = {
    [Field.INPUT]: relevantTokenBalances[0],
    [Field.OUTPUT]: relevantTokenBalances[1],
  }

  const currencies: { [field in Field]?: Currency } = {
    [Field.INPUT]: inputCurrency ?? undefined,
    [Field.OUTPUT]: outputCurrency ?? undefined,
  }

  let inputError: string | undefined
  if (!account) {
    inputError = 'Connect Wallet'
  }

  if (!currencies[Field.INPUT] || !currencies[Field.OUTPUT]) {
    inputError = inputError ?? 'Select a token'
  }

  const swapBlockedByPolicy =
    !isSwapEnabledForCurrency(chainId, currencies[Field.INPUT]) ||
    !isSwapEnabledForCurrency(chainId, currencies[Field.OUTPUT]) ||
    (v2Trade != null && tradeInvolvesSwapDisabledToken(chainId, v2Trade))

  if (swapBlockedByPolicy) {
    inputError = SWAP_DISABLED_ERROR
  }

  if (!parsedAmount) {
    inputError = inputError ?? 'Enter an amount'
  }

  const formattedTo = isAddress(to)
  if (!to || !formattedTo) {
    inputError = inputError ?? 'Enter a recipient'
  } else {
    if (
      BAD_RECIPIENT_ADDRESSES.indexOf(formattedTo) !== -1 ||
      (bestTradeExactIn && involvesAddress(bestTradeExactIn, formattedTo)) ||
      (bestTradeExactOut && involvesAddress(bestTradeExactOut, formattedTo))
    ) {
      inputError = inputError ?? 'Invalid recipient'
    }
  }

  const [allowedSlippage] = useUserSlippageTolerance()

  const slippageAdjustedAmounts =
    v2Trade != null && typeof allowedSlippage === 'number'
      ? computeSlippageAdjustedAmounts(v2Trade, allowedSlippage)
      : undefined

  const [balanceIn, amountIn] = [currencyBalances[Field.INPUT], slippageAdjustedAmounts?.[Field.INPUT] ?? null]

  if (balanceIn && amountIn && balanceIn.lessThan(amountIn)) {
    inputError = 'Insufficient ' + amountIn.currency.symbol + ' balance'
  }

  return {
    currencies,
    currencyBalances,
    parsedAmount,
    v2Trade: v2Trade ?? undefined,
    inputError,
  }
}
